import { logger } from '@inboundx/shared/platform';
import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAppContext } from '../contexts/AppContext';

type ServiceState = 'ok' | 'degraded' | 'down' | 'skipped';

interface ServiceStatus {
    status: ServiceState;
    latency_ms: number;
    detail: string | null;
}

interface StatusResponse {
    status: 'ok' | 'degraded';
    version: string;
    environment: string;
    public_ip: string;
    timestamp: string;
    services: Record<string, ServiceStatus>;
}

const TOKEN_STORAGE_KEY = 'rose:status_probe_token';
const POLL_INTERVAL_MS = 15_000;

const STATE_STYLES: Record<ServiceState, { dot: string; label: string; text: string }> = {
    ok: { dot: 'bg-green-500', label: 'OK', text: 'text-green-700' },
    degraded: { dot: 'bg-amber-500', label: 'DEGRADED', text: 'text-amber-700' },
    down: { dot: 'bg-red-500', label: 'DOWN', text: 'text-red-700' },
    skipped: { dot: 'bg-gray-400', label: 'SKIPPED', text: 'text-gray-600' },
};

export default function StatusIndicatorPage() {
    const { getCurrentApiUrl } = useAppContext();
    const [token, setToken] = useState<string>(() => localStorage.getItem(TOKEN_STORAGE_KEY) ?? '');
    const [data, setData] = useState<StatusResponse | null>(null);
    const [httpStatus, setHttpStatus] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
    const [autoRefresh, setAutoRefresh] = useState(false);

    const fetchStatus = useCallback(async () => {
        const baseUrl = getCurrentApiUrl();
        if (!baseUrl) {
            setError('No API endpoint selected');
            return;
        }
        if (!token) {
            setError('Token required — set X-Status-Token below');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const resp = await fetch(`${baseUrl.replace(/\/$/, '')}/status`, {
                headers: { 'X-Status-Token': token },
            });
            setHttpStatus(resp.status);
            const body = (await resp.json().catch(() => null)) as unknown;
            // 503 wraps StatusResponse in `detail` (FastAPI HTTPException).
            // 200 returns StatusResponse at root.
            // 404 returns `{detail: "Not Found"}` — string detail, no services field.
            const candidate =
                body &&
                typeof body === 'object' &&
                'detail' in body &&
                typeof body.detail === 'object'
                    ? body.detail
                    : body;
            const payload =
                candidate && typeof candidate === 'object' && 'services' in candidate
                    ? (candidate as StatusResponse)
                    : null;
            setData(payload);
            setLastFetchedAt(new Date());
            if (resp.status === 404) {
                setError('404 — wrong token or endpoint missing /status');
            } else if (!payload) {
                setError(
                    `Unexpected HTTP ${resp.status} body: ${JSON.stringify(body).slice(0, 200)}`
                );
            }
        } catch (e) {
            logger.error('status fetch failed', e);
            setError(e instanceof Error ? e.message : String(e));
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [getCurrentApiUrl, token]);

    useEffect(() => {
        if (!autoRefresh) return;
        fetchStatus();
        const id = window.setInterval(fetchStatus, POLL_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [autoRefresh, fetchStatus]);

    const handleTokenChange = (next: string) => {
        setToken(next);
        if (next) localStorage.setItem(TOKEN_STORAGE_KEY, next);
        else localStorage.removeItem(TOKEN_STORAGE_KEY);
    };

    const overallState: ServiceState =
        httpStatus === 503
            ? 'down'
            : data?.status === 'degraded'
              ? 'degraded'
              : data
                ? 'ok'
                : 'skipped';

    return (
        <div className="min-h-screen bg-gray-50 p-6 pl-20">
            <div className="max-w-4xl mx-auto space-y-4">
                <header className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold">Backend Status</h1>
                        <p className="text-sm text-gray-500">
                            Polls <code>/status</code> on the selected API endpoint.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-xs flex items-center gap-1">
                            <input
                                type="checkbox"
                                checked={autoRefresh}
                                onChange={(e) => setAutoRefresh(e.target.checked)}
                            />
                            Auto-refresh (15s)
                        </label>
                        <Button
                            onClick={fetchStatus}
                            disabled={loading}
                            variant="outline"
                            size="sm"
                        >
                            <RefreshCw
                                className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`}
                            />
                            {loading ? 'Checking…' : 'Check now'}
                        </Button>
                    </div>
                </header>

                <Card className="p-4 space-y-2">
                    <label htmlFor="status-token" className="text-xs font-medium block">
                        X-Status-Token (stored in localStorage)
                    </label>
                    <Input
                        id="status-token"
                        type="password"
                        autoComplete="off"
                        value={token}
                        onChange={(e) => handleTokenChange(e.target.value)}
                        placeholder="Paste STATUS_PROBE_TOKEN value"
                    />
                </Card>

                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        <span
                            className={`inline-block h-3 w-3 rounded-full ${STATE_STYLES[overallState].dot}`}
                        />
                        <div className="flex-1">
                            <div
                                className={`text-lg font-medium ${STATE_STYLES[overallState].text}`}
                            >
                                {STATE_STYLES[overallState].label}
                                {httpStatus !== null && (
                                    <span className="ml-2 text-sm text-gray-500">
                                        HTTP {httpStatus}
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-gray-500">
                                {data && (
                                    <>
                                        v{data.version} · {data.environment} · ip {data.public_ip}
                                    </>
                                )}
                                {lastFetchedAt && (
                                    <span className="ml-2">
                                        checked {lastFetchedAt.toLocaleTimeString()}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    {error && <div className="text-sm text-red-700 mt-2">{error}</div>}
                </Card>

                {data?.services && (
                    <Card className="p-0 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-100 text-xs uppercase text-gray-500">
                                <tr>
                                    <th className="text-left px-3 py-2">Service</th>
                                    <th className="text-left px-3 py-2">Status</th>
                                    <th className="text-right px-3 py-2">Latency</th>
                                    <th className="text-left px-3 py-2">Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(data.services).map(([name, svc]) => {
                                    const s = STATE_STYLES[svc.status];
                                    return (
                                        <tr key={name} className="border-t">
                                            <td className="px-3 py-2 font-mono text-xs">{name}</td>
                                            <td className="px-3 py-2">
                                                <span className="inline-flex items-center gap-2">
                                                    <span
                                                        className={`inline-block h-2.5 w-2.5 rounded-full ${s.dot}`}
                                                    />
                                                    <span
                                                        className={`text-xs font-medium ${s.text}`}
                                                    >
                                                        {s.label}
                                                    </span>
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums text-xs">
                                                {svc.latency_ms} ms
                                            </td>
                                            <td className="px-3 py-2 text-xs text-gray-600 break-all">
                                                {svc.detail ?? '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Card>
                )}
            </div>
        </div>
    );
}
